import { generateCombinedCciQuotePDF } from './combinedCciQuotePdf';
import { generateQuotePDF } from './quotePdfGenerator';
import { generateCciQuotePDF } from './cciQuotePdfGenerator';
import { devError } from './devLog';

/**
 * Produces the PDF artefacts for a successful CCI quote.
 *
 * CONF-186 made this one combined document. Because a single document means a
 * single point of failure — where previously a CCI PDF failure still left the
 * AE holding the software quote — any throw degrades to exactly the old
* behaviour: the two standalone PDFs, plus a toast saying so. A failure of
 * either standalone PDF is reported the same way (CONF-240: no alert()).
 *
 * Extracted from App.js so it can be unit-tested; App.js has no working test
 * suite. `deps` is injected for the same reason.
 */
export async function runCciQuoteGeneration(
  { metrics, bom, swRows, cciRows, userName, config, pricingData, showToast },
  deps = { generateCombinedCciQuotePDF, generateQuotePDF, generateCciQuotePDF }
) {
  try {
    await deps.generateCombinedCciQuotePDF({ metrics, bom, swRows, cciRows, userName, config, pricingData });
    return;
  } catch (combinedError) {
    console.error('Combined CCI quote PDF failed; falling back to two PDFs:', combinedError);
    showToast?.({
      badge: 'CCI',
      title: 'Combined PDF unavailable',
      message: 'Could not build the combined quote. Generating the hardware and software quotes as separate files.',
    });
  }

  // Deliberately `swRows`, not `bom`: the fallback reproduces the pre-CONF-186
  // two-file output, where this PDF was the software-only quote. Only the
  // combined document's lead page takes the full priced `bom`.
  try {
    await deps.generateQuotePDF(metrics, swRows, userName, config, pricingData, true, true);
  } catch (swError) {
    devError('Error generating the software quote PDF:', swError);
    showToast?.({
      badge: 'CCI',
      title: 'Software quote PDF failed',
      message: 'Could not generate the software quote PDF: ' + swError.message,
    });
  }

  try {
    await deps.generateCciQuotePDF(cciRows, config, userName);
  } catch (cciPdfError) {
    devError('Error generating CCI manufacturing BOM PDF:', cciPdfError);
    showToast?.({
      badge: 'CCI',
      title: 'CCI manufacturing BOM PDF failed',
      message: 'Could not generate the CCI manufacturing BOM PDF: ' + cciPdfError.message,
    });
  }
}

export default runCciQuoteGeneration;
